import chroma from 'chroma-js'
import { TCountryCode } from 'countries-list'
import * as FlagIcons from 'country-flag-icons/react/3x2'
import { useTranslations } from 'next-intl'
import { ComponentProps } from 'react'
import { ImageWithFallback } from '../../shared/components/image-with-fallback'
import { ISAChip } from './ISAChip'

type FinancialProductSponsor = {
  name?: string
  accentColor?: string
  logoUrl?: string
}

type ISAChipProps = ComponentProps<'div'> & {
  hideSponsorTextIfHasSponsor?: boolean
  availableCountries?: string[]
  financialProductSponsor?: FinancialProductSponsor | null
  showCountryFlags?: boolean
  forceShowISAChip?: boolean
  duotone?: boolean
}

export function ISAChipWithSponsor({
  hideSponsorTextIfHasSponsor,
  availableCountries: _availableCountries,
  financialProductSponsor,
  showCountryFlags,
  forceShowISAChip,
  duotone,
  ...props
}: ISAChipProps) {
  const t = useTranslations('Checkout.ISAChipWithSponsor')

  const hasValidFinancialProductSponsor =
    financialProductSponsor && financialProductSponsor?.name

  const sponsoredText = hasValidFinancialProductSponsor
    ? `${t('powered_by')} ${financialProductSponsor.name}`
    : ''

  // Get flag components for available countries
  const getFlagComponent = (countryCode: string) => {
    const upperCountryCode = countryCode.toUpperCase() as TCountryCode
    const FlagComponent = (FlagIcons as any)[upperCountryCode]
    return FlagComponent ? (
      <FlagComponent key={countryCode} className="h-2" />
    ) : null
  }

  const countryFlags =
    showCountryFlags && _availableCountries?.length
      ? _availableCountries.map(getFlagComponent).filter(Boolean)
      : []

  return !hideSponsorTextIfHasSponsor && financialProductSponsor ? (
    <div
      {...props}
      className={`flex items-center gap-1 rounded-lg bg-mydra-lilas/30 px-2 font-title text-xxs font-normal leading-6 text-mydra-black ${props.className ?? ''}`}
      style={{
        backgroundColor: financialProductSponsor?.accentColor
          ? duotone
            ? chroma(financialProductSponsor?.accentColor).brighten(2.0).hex()
            : financialProductSponsor?.accentColor
          : undefined,
      }}
      title={sponsoredText}
    >
      {!hideSponsorTextIfHasSponsor && hasValidFinancialProductSponsor && (
        <div className="flex flex-wrap items-center justify-center gap-1">
          {countryFlags.length > 0 && (
            <div className="ml-1 flex items-center gap-1">{countryFlags}</div>
          )}
          <span className="mb-auto shrink-0 text-[7px]">
            {financialProductSponsor ? (
              <>
                <span className="font-title text-xxs">
                  {t('isa')} {t('powered_by')}
                </span>
              </>
            ) : (
              t('most_flexible')
            )}
            &nbsp;
          </span>

          {!!financialProductSponsor?.logoUrl && (
            <ImageWithFallback
              src={financialProductSponsor?.logoUrl}
              alt={financialProductSponsor?.name || ''}
              width={37}
              height={13}
              className="object-scale-down"
              fallback={financialProductSponsor?.name}
            />
          )}
        </div>
      )}

      {(forceShowISAChip ||
        hideSponsorTextIfHasSponsor ||
        !hasValidFinancialProductSponsor) && (
        <ISAChip
          className="-mr-1 rounded-md"
          style={{
            backgroundColor: financialProductSponsor?.accentColor,
          }}
        />
      )}
    </div>
  ) : (
    <ISAChip
      style={{
        backgroundColor: financialProductSponsor?.accentColor,
      }}
    />
  )
}
